import React, { useState } from 'react';
import './Perfil.css';
import { apiFetch, ApiError } from './api';
import { useRecurso } from './hooks/useRecurso';
import EstadoCarregando from './EstadoCarregando';
import EstadoErro from './EstadoErro';

// Mensagens pt-BR por código de erro do backend.
// Perfil: PUT /api/usuarios/me (PRD 05). Senha: POST /api/usuarios/me/senha (PRD 06).
const MENSAGENS_PERFIL = {
  VALIDATION_ERROR: 'Verifique os campos: nome (2 a 120 caracteres), telefone e cargo válidos.',
};

const MENSAGENS_SENHA = {
  INVALID_PASSWORD: 'A senha atual está incorreta.',
  PASSWORD_MISMATCH: 'A confirmação não confere com a nova senha.',
  SAME_AS_CURRENT: 'A nova senha deve ser diferente da senha atual.',
  WEAK_PASSWORD: 'A nova senha não atende aos requisitos de segurança.',
  VALIDATION_ERROR: 'Preencha a senha atual e a nova senha.',
  RATE_LIMITED: 'Muitas tentativas. Aguarde alguns minutos e tente novamente.',
};

function mensagemErro(erro, mapa, generica) {
  if (erro instanceof ApiError) {
    if (erro.status === 429) return mapa.RATE_LIMITED || generica;
    if (erro.code && mapa[erro.code]) return mapa[erro.code];
  }
  return generica;
}

// Extrai a lista de regras de senha do detalhe do WEAK_PASSWORD.
function regrasSenha(erro) {
  if (erro instanceof ApiError && erro.code === 'WEAK_PASSWORD') {
    const regras = erro.details?.regras;
    if (Array.isArray(regras) && regras.length) return regras;
  }
  return null;
}

const SENHA_VAZIA = { senhaAtual: '', novaSenha: '', confirmaSenha: '' };

function Perfil({ usuario, onPerfilAtualizado }) {
  const { dados, carregando, erro, recarregar } = useRecurso(
    () => apiFetch('/api/usuarios/me'),
    [],
  );

  const [modoEdicao, setModoEdicao] = useState(false);
  const [form, setForm] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [erroSalvar, setErroSalvar] = useState('');
  const [sucesso, setSucesso] = useState('');

  // Mudar senha
  const [mostrarFormSenha, setMostrarFormSenha] = useState(false);
  const [senhaForm, setSenhaForm] = useState(SENHA_VAZIA);
  const [salvandoSenha, setSalvandoSenha] = useState(false);
  const [erroSenha, setErroSenha] = useState('');
  const [regrasSenhaErro, setRegrasSenhaErro] = useState(null);
  const [sucessoSenha, setSucessoSenha] = useState('');

  // Exibe loading/erro apenas na carga inicial (sem dados ainda).
  // Quando `recarregar` é chamado após uma edição, `dados` já existe e
  // o formulário permanece visível (loading inline) para não sumir a mensagem de sucesso.
  if (carregando && !dados) {
    return (
      <div className="perfil-container">
        <EstadoCarregando mensagem="Carregando perfil..." />
      </div>
    );
  }

  if (!dados && erro) {
    return (
      <div className="perfil-container">
        <EstadoErro
          mensagem="Não foi possível carregar o seu perfil."
          onRetry={recarregar}
        />
      </div>
    );
  }

  const perfil = dados || {};

  const iniciarEdicao = () => {
    setForm({
      nome: perfil.nome || '',
      telefone: perfil.telefone || '',
      cargo: perfil.cargo || '',
    });
    setErroSalvar('');
    setSucesso('');
    setModoEdicao(true);
  };

  const cancelarEdicao = () => {
    setModoEdicao(false);
    setForm(null);
    setErroSalvar('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSalvando(true);
    setErroSalvar('');
    setSucesso('');
    try {
      // Backend ignora `email` no corpo (troca de e-mail tem fluxo próprio);
      // envia apenas os campos editáveis do perfil.
      const atualizado = await apiFetch('/api/usuarios/me', {
        method: 'PUT',
        body: {
          nome: form.nome,
          telefone: form.telefone,
          cargo: form.cargo,
        },
      });
      recarregar();
      setModoEdicao(false);
      setForm(null);
      setSucesso('Perfil atualizado com sucesso.');
      if (typeof onPerfilAtualizado === 'function') {
        onPerfilAtualizado({
          nome: atualizado?.nome,
          cargo: atualizado?.cargo,
          avatar: atualizado?.avatar,
        });
      }
    } catch (error) {
      setErroSalvar(
        mensagemErro(
          error,
          MENSAGENS_PERFIL,
          'Não foi possível salvar as alterações. Tente novamente.',
        ),
      );
    } finally {
      setSalvando(false);
    }
  };

  const handleSenhaChange = (e) => {
    const { name, value } = e.target;
    setSenhaForm((prev) => ({ ...prev, [name]: value }));
  };

  const abrirFormSenha = () => {
    setSenhaForm(SENHA_VAZIA);
    setErroSenha('');
    setRegrasSenhaErro(null);
    setSucessoSenha('');
    setMostrarFormSenha(true);
  };

  const fecharFormSenha = () => {
    setMostrarFormSenha(false);
    setSenhaForm(SENHA_VAZIA);
    setErroSenha('');
    setRegrasSenhaErro(null);
  };

  const handleSubmitSenha = async (e) => {
    e.preventDefault();
    setErroSenha('');
    setRegrasSenhaErro(null);
    setSucessoSenha('');

    if (senhaForm.novaSenha !== senhaForm.confirmaSenha) {
      setErroSenha('A confirmação não confere com a nova senha.');
      return;
    }

    setSalvandoSenha(true);
    try {
      await apiFetch('/api/usuarios/me/senha', {
        method: 'POST',
        body: {
          senhaAtual: senhaForm.senhaAtual,
          novaSenha: senhaForm.novaSenha,
          confirmaSenha: senhaForm.confirmaSenha,
        },
      });
      setMostrarFormSenha(false);
      setSenhaForm(SENHA_VAZIA);
      setSucessoSenha('Senha alterada com sucesso.');
    } catch (error) {
      setErroSenha(
        mensagemErro(
          error,
          MENSAGENS_SENHA,
          'Não foi possível alterar a senha. Tente novamente.',
        ),
      );
      setRegrasSenhaErro(regrasSenha(error));
    } finally {
      setSalvandoSenha(false);
    }
  };

  const inicial =
    (perfil.nome || usuario?.nome || 'U').charAt(0).toUpperCase();

  return (
    <div className="perfil-container">
      <div className="perfil-header">
        <h1>Meu Perfil</h1>
        {!modoEdicao && (
          <button
            className="btn btn-primary"
            onClick={iniciarEdicao}
          >
            Editar Perfil
          </button>
        )}
      </div>

      <div className="perfil-content">
        <div className="perfil-foto-container">
          <div className="perfil-foto">
            {perfil.avatar ? (
              <img src={perfil.avatar} alt="Foto do perfil" />
            ) : (
              <div className="foto-placeholder">{inicial}</div>
            )}
          </div>
        </div>

        {sucesso && (
          <p className="perfil-feedback perfil-feedback--ok" role="status">
            {sucesso}
          </p>
        )}

        <form onSubmit={handleSubmit} className="perfil-form">
          <div className="form-grupo">
            <label htmlFor="nome">Nome Completo</label>
            <input
              type="text"
              id="nome"
              name="nome"
              value={modoEdicao ? form.nome : perfil.nome || ''}
              onChange={handleInputChange}
              disabled={!modoEdicao}
            />
          </div>

          <div className="form-grupo">
            <label htmlFor="email">E-mail</label>
            <input
              type="email"
              id="email"
              name="email"
              value={perfil.email || ''}
              disabled
            />
            {perfil.emailPendente && (
              <small className="form-aviso">
                Troca de e-mail pendente de confirmação:{' '}
                {perfil.emailPendente}
              </small>
            )}
          </div>

          <div className="form-grupo">
            <label htmlFor="telefone">Telefone</label>
            <input
              type="tel"
              id="telefone"
              name="telefone"
              value={modoEdicao ? form.telefone : perfil.telefone || ''}
              onChange={handleInputChange}
              disabled={!modoEdicao}
            />
          </div>

          <div className="form-grupo">
            <label htmlFor="cargo">Cargo</label>
            <input
              type="text"
              id="cargo"
              name="cargo"
              value={modoEdicao ? form.cargo : perfil.cargo || ''}
              onChange={handleInputChange}
              disabled={!modoEdicao}
            />
          </div>

          {erroSalvar && (
            <p className="perfil-feedback perfil-feedback--erro" role="alert">
              {erroSalvar}
            </p>
          )}

          {modoEdicao && (
            <div className="perfil-acoes">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={cancelarEdicao}
                disabled={salvando}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={salvando}
              >
                {salvando ? 'Salvando...' : 'Salvar Alterações'}
              </button>
            </div>
          )}
        </form>

        <div className="perfil-seguranca">
          <h2>Segurança</h2>
          {sucessoSenha && (
            <p className="perfil-feedback perfil-feedback--ok" role="status">
              {sucessoSenha}
            </p>
          )}
          {!mostrarFormSenha && (
            <button className="btn btn-outline" onClick={abrirFormSenha}>
              Alterar Senha
            </button>
          )}

          {mostrarFormSenha && (
            <form onSubmit={handleSubmitSenha} className="perfil-form-senha">
              <div className="form-grupo">
                <label htmlFor="senhaAtual">Senha atual</label>
                <input
                  type="password"
                  id="senhaAtual"
                  name="senhaAtual"
                  value={senhaForm.senhaAtual}
                  onChange={handleSenhaChange}
                  autoComplete="current-password"
                />
              </div>
              <div className="form-grupo">
                <label htmlFor="novaSenha">Nova senha</label>
                <input
                  type="password"
                  id="novaSenha"
                  name="novaSenha"
                  value={senhaForm.novaSenha}
                  onChange={handleSenhaChange}
                  autoComplete="new-password"
                />
              </div>
              <div className="form-grupo">
                <label htmlFor="confirmaSenha">Confirmar nova senha</label>
                <input
                  type="password"
                  id="confirmaSenha"
                  name="confirmaSenha"
                  value={senhaForm.confirmaSenha}
                  onChange={handleSenhaChange}
                  autoComplete="new-password"
                />
              </div>

              {erroSenha && (
                <div
                  className="perfil-feedback perfil-feedback--erro"
                  role="alert"
                >
                  <p>{erroSenha}</p>
                  {regrasSenhaErro && (
                    <ul className="perfil-regras-senha">
                      {regrasSenhaErro.map((regra) => (
                        <li key={regra}>{regra}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              <div className="perfil-acoes">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={fecharFormSenha}
                  disabled={salvandoSenha}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={salvandoSenha}
                >
                  {salvandoSenha ? 'Salvando...' : 'Alterar Senha'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default Perfil;
